// ============================================================================
// CrewAhead Maritime Platform - API Type Definitions
// ============================================================================

import type {
  Certification,
  CompanyProfile,
  Conversation,
  JobApplication,
  JobPost,
  Message,
  Notification,
  SeaServiceRecord,
  SeafarerProfile,
  ShoreProfile,
  User,
} from './models';

// ─── Generic API Response ────────────────────────────────────────────────────
// Note: Django REST Framework returns data directly without a wrapper envelope.
// We keep ApiResponse<T> for endpoints that DO return wrapped responses (e.g.,
// custom API views), but most DRF endpoints return T directly.

export interface ApiResponse<T = unknown> {
  success: boolean;
  data: T;
  message: string | null;
  meta?: ResponseMeta;
}

export interface ResponseMeta {
  timestamp: string;
  requestId: string;
  version: string;
}

// ─── Paginated Response (Django REST Framework format) ───────────────────────

export interface PaginatedResponse<T = unknown> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
  // Hook-friendly aliases for backward compatibility
  data: T[];
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  };
}

// ─── Pagination Params ───────────────────────────────────────────────────────

export interface PaginationParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// ─── API Error ───────────────────────────────────────────────────────────────
// Django REST Framework error formats:
// - Single error: { "detail": "Error message" }
// - Field errors: { "field_name": ["error message"] }
// - Non-field errors: { "non_field_errors": ["error message"] }

export interface ApiError {
  statusCode: number;
  message: string;
  code: string;
  details: Record<string, string[]> | null;
  timestamp: string;
}

export interface ValidationError {
  field: string;
  message: string;
  code: string;
}

// ─── Auth Types ──────────────────────────────────────────────────────────────

export interface LoginCredentials {
  email: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: 'seafarer' | 'shore' | 'employer' | 'institute';
  phone?: string;
  agreedToTerms: boolean;
  companyName?: string; // required if role is employer
  instituteName?: string; // required if role is institute
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface RefreshTokenResponse {
  accessToken: string;
  refreshToken: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  newPassword: string;
  confirmPassword: string;
}

export interface VerifyEmailData {
  token: string;
}

/**
 * Response from POST /auth/verify-email.
 * `code` distinguishes first-time verification ("verified") from an
 * idempotent repeat ("already_verified"). Failure responses carry
 * "invalid_token" or "expired_token" with a 400 status.
 */
export interface VerifyEmailResponse {
  detail: string;
  code?: 'verified' | 'already_verified' | 'invalid_token' | 'expired_token';
}

export interface ResendVerificationData {
  email: string;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
}

// ─── Profile Types ───────────────────────────────────────────────────────────

export type ProfileUpdateData =
  | Partial<SeafarerProfile>
  | Partial<ShoreProfile>
  | Partial<CompanyProfile>;

export interface UploadDocumentData {
  file: File;
  type: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  issuingAuthority?: string;
}

export interface SeaServiceCreateData {
  vesselName: string;
  vesselType: number | null;
  imoNumber?: string;
  flag?: string;
  dwt?: number;
  grt?: number;
  engineType?: string;
  enginePowerKw?: number;
  rank: number | null;
  rankOther?: string;
  employerName: string;
  agencyName?: string;
  dateFrom: string;
  dateTo?: string;
  isCurrent: boolean;
  tradingArea?: string;
  reasonForLeaving?: string;
  dutiesSummary?: string;
}

export interface CertificationCreateData {
  certNameCustom: string;
  certificationTypeId: number;
  certificationName: string;
  issuingAuthority: string;
  certificateNumber: string;
  issueDate: string;
  expiryDate?: string;
  notes?: string;
  documentFile?: File;
}

// ─── Job Types ───────────────────────────────────────────────────────────────

export interface JobFilters {
  keywords?: string;
  rank?: string;
  rankType?: 'seafarer' | 'shore';
  vesselType?: string;
  contractType?: string;
  location?: string;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  experienceYears?: number;
  tradingArea?: string;
  nationality?: string;
  postedWithin?: '24h' | '3d' | '7d' | '14d' | '30d';
  isUrgent?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'relevance' | 'newest' | 'salary_high' | 'salary_low' | 'closing_soon';
}

export interface JobApplicationCreateData {
  coverLetter?: string;
  proposedSalary?: number;
  availableFrom?: string;
  notes?: string;
  cvId?: string;
}

export interface JobAlertCreateData {
  name: string;
  keywords?: string[];
  ranks?: string[];
  rankType: 'seafarer' | 'shore';
  vesselTypes?: string[];
  contractTypes?: string[];
  locations?: string[];
  salaryMin?: number;
  salaryCurrency?: string;
  frequency: 'daily' | 'weekly' | 'instant';
}

// ─── Notification Types ──────────────────────────────────────────────────────

export interface NotificationFilters {
  isRead?: boolean;
  type?: string;
  priority?: string;
  page?: number;
  limit?: number;
}

export interface MarkNotificationsReadData {
  notificationIds: string[];
}

// ─── Search / Discovery Types ────────────────────────────────────────────────

export interface SearchSuggestion {
  type: 'rank' | 'vessel' | 'company' | 'location';
  value: string;
  count: number;
}

// ─── Dashboard Types ─────────────────────────────────────────────────────────

export interface SeafarerDashboardStats {
  totalApplications: number;
  pendingApplications: number;
  profileCompleteness: number;
  totalSeaTimeMonths: number;
  savedJobsCount: number;
  upcomingDocumentExpiries: number;
  unreadNotifications: number;
}

export interface EmployerDashboardStats {
  activeJobs: number;
  totalApplications: number;
  newApplicationsThisWeek: number;
  interviewsScheduled: number;
  jobsFilledThisMonth: number;
  companyProfileViews: number;
  unreadNotifications: number;
}

// ─── API Function Return Types (Direct - DRF returns data without wrapper) ───

export type GetProfileResponse = {
  user: User;
  profile: SeafarerProfile | ShoreProfile | CompanyProfile;
};

export type UpdateProfileResponse = {
  user: User;
  profile: SeafarerProfile | ShoreProfile | CompanyProfile;
};

export type GetJobsResponse = PaginatedResponse<JobPost>;

export type GetJobResponse = JobPost;

export type GetApplicationsResponse = PaginatedResponse<JobApplication>;

export type GetSavedJobsResponse = PaginatedResponse<JobPost>;

export type GetSeaServiceResponse = SeaServiceRecord[];

export type GetCertificationsResponse = Certification[];

export type GetNotificationsResponse = PaginatedResponse<Notification>;

export type GetConversationsResponse = PaginatedResponse<Conversation>;

export type GetMessagesResponse = PaginatedResponse<Message>;

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const QUERY_KEYS = {
  AUTH: {
    USER: ['auth', 'user'] as const,
    SESSION: ['auth', 'session'] as const,
  },
  PROFILE: {
    DETAIL: (userId: string) => ['profile', userId] as const,
    SEA_SERVICE: (userId: string) => ['profile', 'sea-service', userId] as const,
    CERTIFICATIONS: (userId: string) =>
      ['profile', 'certifications', userId] as const,
    DOCUMENTS: (userId: string) => ['profile', 'documents', userId] as const,
  },
  JOBS: {
    LIST: (filters: Record<string, unknown>) =>
      ['jobs', 'list', filters] as const,
    DETAIL: (jobId: string) => ['jobs', 'detail', jobId] as const,
    APPLICATIONS: ['jobs', 'applications'] as const,
    SAVED: ['jobs', 'saved'] as const,
    ALERTS: ['jobs', 'alerts'] as const,
  },
  NOTIFICATIONS: {
    LIST: (filters: Record<string, unknown>) =>
      ['notifications', 'list', filters] as const,
    UNREAD_COUNT: ['notifications', 'unread-count'] as const,
  },
  MESSAGES: {
    CONVERSATIONS: ['messages', 'conversations'] as const,
    LIST: (conversationId: string) =>
      ['messages', 'list', conversationId] as const,
  },
  TAXONOMY: {
    REGISTRY: ['taxonomy', 'registry'] as const,
    CATEGORY: (category: string) => ['taxonomy', 'category', category] as const,
    ITEM: (category: string, code: string) =>
      ['taxonomy', 'item', category, code] as const,
  },
  INSTITUTE: {
    PROFILE: ['institute', 'profile'] as const,
    OFFERINGS: (params?: { page?: number; pageSize?: number; search?: string }) =>
      ['institute', 'offerings', params ?? {}] as const,
    BATCHES: (params?: { page?: number; pageSize?: number; offeringId?: number }) =>
      ['institute', 'batches', params ?? {}] as const,
    ENQUIRIES: (params?: { page?: number; pageSize?: number; status?: string }) =>
      ['institute', 'enquiries', params ?? {}] as const,
    ENQUIRY_DETAIL: (id: string | number) => ['institute', 'enquiry', String(id)] as const,
    RESOURCES: (params?: { page?: number; pageSize?: number; search?: string }) =>
      ['institute', 'resources', params ?? {}] as const,
    RESOURCE_DETAIL: (id: string | number) => ['institute', 'resource', String(id)] as const,
    RESOURCE_ENQUIRIES: (params?: { page?: number; pageSize?: number; status?: string }) =>
      ['institute', 'resource-enquiries', params ?? {}] as const,
    RESOURCE_ENQUIRY_DETAIL: (id: string | number) =>
      ['institute', 'resource-enquiry', String(id)] as const,
  },
  MATCHING: {
    JOBS: ['matching', 'jobs'] as const,
    JOBS_FILTERED: (filters: Record<string, unknown>) => ['matching', 'jobs', filters] as const,
    SCORE: (jobId: string) => ['matching', 'score', jobId] as const,
    CANDIDATES: (jobId: string) => ['matching', 'candidates', jobId] as const,
  },
  CAREER: {
    PROFILE: ['career', 'profile'] as const,
    READINESS: (positionId?: number) => ['career', 'readiness', positionId ?? 'default'] as const,
    PATH: (positionId?: number) => ['career', 'path', positionId ?? 'default'] as const,
    SKILL_GAPS: (positionId?: number) => ['career', 'skill-gaps', positionId ?? 'default'] as const,
    COURSES: (positionId?: number) => ['career', 'courses', positionId ?? 'default'] as const,
    RECOMMENDATIONS: (positionId?: number) => ['career', 'recommendations', positionId ?? 'default'] as const,
    TARGET_ROLES: ['career', 'target-roles'] as const,
    SUMMARY: ['career', 'summary'] as const,
    SHORE_JOBS: ['career', 'shore-jobs'] as const,
    BARRIERS: ['career', 'barriers'] as const,
    TRANSFERABLE_SKILLS: ['career', 'transferable-skills'] as const,
    SEA_TO_SHORE_PATHS: ['career', 'sea-to-shore-paths'] as const,
    SHORE_TO_SEA_PATHS: ['career', 'shore-to-sea-paths'] as const,
    PROMOTION_READINESS: ['career', 'promotion-readiness'] as const,
  },
  AVAILABILITY: {
    MY_POSTS: ['availability', 'my-posts'] as const,
    LIST: (filters: Record<string, unknown>) => ['availability', 'list', filters] as const,
    DETAIL: (postId: string) => ['availability', 'detail', postId] as const,
  },
} as const;
